import {
  inject,
  computed,
  Component,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Signal,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ActivatedRoute } from '@angular/router';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';

import { Store, select } from '@ngrx/store';

import { TranslateService } from '@ngx-translate/core';

import { UtilsService } from 'src/app/core/services/utils.service';
import { ObjectActionsService } from 'src/app/core/services/object-actions.service';
import { ObjectCreatorService } from 'src/app/core/services/object-creator.service';

import {
  Feed,
  Dimension,
  Dashboard,
  DataObject,
} from 'src/app/core/models/core.models';
import { NomObjectClass, SortDirection } from 'src/app/core/constants';

import { objectChanged } from 'src/app/core/actions/core.actions';
import {
  selectObjects,
  clearSelectedObjects,
} from 'src/app/core/actions/selected.actons';
import {
  setWorkspaceSortingField,
  setWorkspaceFilterString,
  setWorkspaceObjectsLifeCycle,
  setWorkspaceSortingDirection,
} from 'src/app/core/actions/workspace.actions';

import {
  selectCurrentCompanyId,
  selectSelectedObjects,
  selectUserWorkspaceContextSettings,
  selectWorkspaceFilterString,
  selectWorkspaceSortingField,
  selectWorkspaceSortingDirection,
  selectUser,
  selectWorspaceObjectsLifeCycle,
} from 'src/app/core/reducers';
import { UserApiService } from 'src/app/api/queries/user-api.service';
import { RecentObjectsService } from 'src/app/core/services/recent-objects.service';
import { AppState } from 'src/app/reducers';
import {
  WorkspaceObjectsScope,
  injectWorkspaceObjectsQuery,
} from 'src/app/api/queries/workspace.queries';
import {
  UserViewOptionsScope,
  injectToggleUserViewOption,
  injectWorkspaceViewOptionsQuery,
} from 'src/app/api/queries/view-preset.queries';
import { ViewId } from '@services/view-preset.service';

import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  map,
  take,
  tap,
} from 'rxjs';
import { PropertyViewConfig } from 'src/app/core/services/feed-settings.service';

/** A row in a projected toolbar dropdown panel (view options / sort). */
interface ToolbarMenuItem {
  text?: string;
  icon?: IconProp;
  value?: unknown;
  label?: string;
  isToggle?: boolean;
  isRadio?: boolean;
  isSelected?: boolean;
  radioGroup?: string;
}

/** A run of consecutive same-`radioGroup` rows, or a single toggle row. */
interface ToolbarMenuGroup {
  radioGroup?: string;
  /** Currently selected `value` within a radio run. */
  value?: unknown;
  items: ToolbarMenuItem[];
}

/** A command row in the "apply to selected" dropdown (from ObjectActionsService). */
interface ApplyAction {
  text: string;
  icon?: IconProp;
  // eslint-disable-next-line @typescript-eslint/ban-types
  action?: Function; // matches ObjectActionsService.getButtonForAction's action
}

@Component({
  selector: 'app-workspace',
  templateUrl: './workspace.component.html',
  styleUrls: ['./workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkspaceComponent {
  /**
   * The workspace's three object lists. This component owns the query and is its
   * only reactive observer - the widget sidebar's feed picker goes through
   * `WorkspaceObjectsService`.
   */
  dashboards: Signal<Array<Dashboard>>;
  feeds: Signal<Array<Feed>>;
  dimensions: Signal<Array<Dimension>>;
  isLoading: Signal<boolean>;

  currentTab: 'active' | 'archived' | 'deleted' = 'active';
  filterString: string;
  viewConfig: PropertyViewConfig[] = [];

  /** `wv-objects` - the view-options blob this screen renders and toggles. */
  private viewOptionsScope: Signal<UserViewOptionsScope | null>;
  private viewOptions$: Observable<PropertyViewConfig[]>;
  private readonly toggleViewOption = injectToggleUserViewOption(
    ViewId.WorkspaceViewObjects,
  );

  // * Toolbar state (migrated from WorkspaceActionsService)
  selectAllChecked = false;
  lifecycleStatus = 1;
  sortField = 'title';
  sortDirection: SortDirection = 'asc';
  viewItems: ToolbarMenuItem[] = [];
  viewGroups: ToolbarMenuGroup[] = [];
  applyActions: ApplyAction[] = [];
  applyCount = 0;

  private readonly statusLabels: Record<number, string> = {
    1: 'workspace-actions.button.active',
    3: 'workspace-actions.button.archived',
    4: 'workspace-actions.button.deleted',
  };
  private readonly statusIcons: Record<number, IconProp> = {
    1: ['fas', 'check'],
    3: ['fas', 'box-archive'],
    4: ['fas', 'trash'],
  };

  get sortDirIcon(): IconProp {
    return this.sortDirection === 'asc'
      ? ['fas', 'sort-amount-up']
      : ['fas', 'sort-amount-down'];
  }
  get sortFieldIcon(): IconProp {
    return this.sortField === 'time_created' ||
      this.sortField === 'time_modified'
      ? ['fas', 'clock']
      : ['fas', 'text-height'];
  }
  get statusLabel(): string {
    return this.translateService.instant(
      this.statusLabels[this.lifecycleStatus] ?? this.statusLabels[1],
    );
  }
  get statusIcon(): IconProp {
    return this.statusIcons[this.lifecycleStatus] ?? this.statusIcons[1];
  }
  get applyLabel(): string {
    return `${this.translateService.instant('workspace-actions.button.apply-to-selected')} (${this.applyCount})`;
  }
  get sortLabel(): string {
    return this.translateService.instant('workspace-actions.button.sort');
  }
  get viewLabel(): string {
    return this.translateService.instant('workspace-actions.button.view');
  }

  private destroyRef = inject(DestroyRef);
  private readonly userApi = inject(UserApiService);
  private readonly recentObjects = inject(RecentObjectsService);

  public constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private objectCreatorService: ObjectCreatorService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    public utilService: UtilsService,
    public objectActionService: ObjectActionsService,
  ) {
    // * the Active / Archived / Deleted filter is a query-key term, read from the
    // * store rather than from the route, so it survives leaving the screen and
    // * coming back.
    //
    // * `workspaceId` follows the route **param stream**, not the snapshot: this
    // * route reuses the component when only the id changes (e.g. picking another
    // * workspace out of Recent), and a snapshot would then leave the key - and the
    // * filter below - pointing at the workspace the user just left.
    const workspaceId = toSignal(
      this.route.params.pipe(map((params) => params['workspaceId'] as string)),
      { initialValue: this.route.snapshot.params['workspaceId'] as string },
    );
    const companyId = this.store.selectSignal(selectCurrentCompanyId);
    const lifecycleStatus = this.store.selectSignal(
      selectWorspaceObjectsLifeCycle,
    );

    const scope = computed<WorkspaceObjectsScope | null>(() => {
      const id = workspaceId();

      return id
        ? {
            workspaceId: id,
            companyId: companyId(),
            lifecycleStatus: lifecycleStatus(),
          }
        : null;
    });

    const objectsQuery = injectWorkspaceObjectsQuery(scope);

    // * keep this workspace's objects, apply the search string, then sort. The
    // * workspace filter is redundant with the query key for freshly fetched rows,
    // * but not for the cache patches - a moved object keeps its row until the next
    // * fetch and leaves the list through exactly this test.
    const filterString = this.store.selectSignal(selectWorkspaceFilterString);
    const sortingField = this.store.selectSignal(selectWorkspaceSortingField);
    const sortingDirection = this.store.selectSignal(
      selectWorkspaceSortingDirection,
    );

    const withOptions = <T extends DataObject>(
      source: Signal<T[] | undefined>,
    ): Signal<T[]> =>
      computed(() => {
        const normalizedFilterString = (filterString() ?? '').toLowerCase();

        const objects = (source() ?? []).filter((obj) => {
          if (obj.workspace_id !== workspaceId()) return false;
          if (!normalizedFilterString) return true;

          const filterWords = normalizedFilterString.split(/\s+/);
          const title = obj.title.toLowerCase();
          const description = obj.description.toLowerCase();

          return filterWords.every(
            (word) => title.includes(word) || description.includes(word),
          );
        });

        const field = sortingField();
        if (field) {
          const direction = sortingDirection();
          const value = (object: T) =>
            (object as unknown as Record<string, unknown>)[field];

          objects.sort((a, b) =>
            direction === 'asc'
              ? value(a) > value(b)
                ? 1
                : -1
              : value(a) < value(b)
                ? 1
                : -1,
          );
        }

        return objects;
      });

    this.dashboards = withOptions(
      computed(() => objectsQuery.data()?.dashboards),
    );
    this.feeds = withOptions(computed(() => objectsQuery.data()?.feeds));
    this.dimensions = withOptions(
      computed(() => objectsQuery.data()?.dimensions),
    );
    this.isLoading = objectsQuery.isLoading;

    // * the view options (`wv-objects`) are per **user**, not per workspace, so the
    // * key does not carry `workspaceId` - walking between workspaces reuses the
    // * same entry
    const user = this.store.selectSignal(selectUser);

    this.viewOptionsScope = computed<UserViewOptionsScope | null>(() => {
      const currentUser = user();

      return currentUser?.id
        ? {
            userId: currentUser.id,
            companyId: companyId(),
            user: currentUser,
          }
        : null;
    });

    const viewOptionsQuery = injectWorkspaceViewOptionsQuery(
      this.viewOptionsScope,
    );

    // * built here because this is an injection context; `setupToolbar` runs from
    // * `ngOnInit` and only subscribes
    this.viewOptions$ = toObservable(
      computed(() => viewOptionsQuery.data() ?? []),
    );

    this.viewOptions$
      .pipe(
        tap((config) => {
          this.viewConfig = config;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.route.data
      .pipe(
        tap((data) => {
          if (data['workspace']) {
            this.store.dispatch(objectChanged({ object: data['workspace'] }));
            this.recentObjects.record(data['workspace']);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.setupToolbar();
  }

  public setupToolbar(): void {
    this.store
      .pipe(
        select(selectWorspaceObjectsLifeCycle),
        distinctUntilChanged(),
        tap((status) => {
          this.lifecycleStatus = this.statusLabels[status] ? status : 1;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    combineLatest([
      this.store.pipe(
        select(selectWorkspaceSortingDirection),
        distinctUntilChanged(),
      ),
      this.store.pipe(
        select(selectWorkspaceSortingField),
        distinctUntilChanged(),
      ),
    ])
      .pipe(
        tap(([dir, field]) => {
          this.sortDirection = dir;
          this.sortField = field;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.viewOptions$
      .pipe(
        tap((config) => {
          this.viewItems = config.map((c) => ({
            text: c.label,
            label: c.label,
            icon: c.icon,
            value: c.path,
            isToggle: !c.isRadio,
            isRadio: c.isRadio,
            isSelected: c.selected,
            radioGroup: c.radioGroup,
          }));
          this.viewGroups = this.groupViewItems(this.viewItems);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .pipe(
        select(selectSelectedObjects),
        tap((selectedObjects) => {
          this.applyCount = selectedObjects.size;
          this.updateApplyActions(selectedObjects);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectWorkspaceFilterString)
      .pipe(
        tap((filterString) => {
          this.filterString = filterString;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private updateApplyActions(selectedObjects: Map<string, DataObject>): void {
    let applicable: string[] = [];
    selectedObjects.forEach((object) => {
      // Workspace excludes objects the user can't edit.
      if (object.can_edit === false) {
        return;
      }
      let classId = object.object_class_id;
      const typeId = object.object_type_id || null;
      // widget placeholders set object_class_id/object_id to the same value
      if (object['widget_type_id'] !== undefined) {
        classId = NomObjectClass.WIDGET;
      }
      const actions =
        this.objectActionService.objectActions[classId]?.[
          object.lifecycle_status
        ]?.[typeId] ?? null;
      if (actions) {
        const enabled = Object.keys(actions).filter((a) => actions[a] === true);
        applicable =
          applicable.length === 0
            ? enabled
            : applicable.filter((a) => enabled.includes(a));
      }
    });

    this.applyActions = applicable.map((action) => {
      const button = this.objectActionService.getButtonForAction(action);
      return {
        text: button.label,
        icon: button.leadingIcon,
        action: button.action,
      };
    });
  }

  onSelectAllChange(selected: boolean): void {
    this.selectAllChecked = selected;
    this.selectAllObjects(selected);
  }

  onStatusChange(status: number): void {
    this.lifecycleStatus = status;
    const workspaceId = this.route.snapshot.params['workspaceId'];
    this.store.dispatch(
      setWorkspaceObjectsLifeCycle({
        workspaceId,
        workspaceObjectsLifeCycle: status,
      }),
    );
  }

  onSortFieldChange(sortingField: string): void {
    this.sortField = sortingField;
    this.store.dispatch(setWorkspaceSortingField({ sortingField }));
    this.persistSorting();
  }

  onSortDirectionChange(sortingDirection: SortDirection): void {
    this.sortDirection = sortingDirection;
    this.store.dispatch(setWorkspaceSortingDirection({ sortingDirection }));
    this.persistSorting();
  }

  // Fire-and-forget: dispatch is synchronous, so the selector already sees the change.
  private persistSorting(): void {
    this.store
      .select(selectUserWorkspaceContextSettings)
      .pipe(take(1))
      .subscribe((contextSettings) => {
        this.userApi.saveContextValue(contextSettings).subscribe({
          error: (error) =>
            console.error('Failed to persist workspace sorting', error),
        });
      });
  }

  onViewToggle(item: ToolbarMenuItem): void {
    const scope = this.viewOptionsScope();
    if (!scope) return;

    this.toggleViewOption(scope, item.label);
  }

  /** Group consecutive same-`radioGroup` rows so each run gets one radiogroup wrapper. */
  private groupViewItems(items: ToolbarMenuItem[]): ToolbarMenuGroup[] {
    const groups: ToolbarMenuGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        if (item.isSelected) {
          previous.value = item.value;
        }
        continue;
      }

      groups.push({
        radioGroup: item.radioGroup,
        value: item.isSelected ? item.value : undefined,
        items: [item],
      });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: ToolbarMenuGroup, value: unknown): void {
    const item = group.items.find((i) => i.value === value);
    if (item) {
      this.onViewToggle(item);
    }
  }

  handleObjectSearchChange(query: string) {
    // Use the debounced function instead of the original search function

    if (typeof query == 'string') {
      this.filterString = query;
      this.store.dispatch(
        setWorkspaceFilterString({ filterString: this.filterString }),
      );
    }
  }

  createNewDashboard() {
    this.objectCreatorService.createNewDashboard();
  }

  createNewFeed() {
    this.objectCreatorService.createNewFeed();
  }

  createNewDimension() {
    this.objectCreatorService.createNewDimension();
  }

  selectAllObjects(selected: boolean) {
    const objects = [
      ...this.dashboards(),
      ...this.feeds(),
      ...this.dimensions(),
    ];
    if (selected) {
      this.store.dispatch(selectObjects({ objects }));
    } else {
      this.store.dispatch(clearSelectedObjects());
    }
  }

  trackById(index: number, object: DataObject): any {
    return object.id;
  }
}
